
import React from "react";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import FormActions from "@/components/shared/FormActions";
import { ContactInfoFields } from "./ContactInfoFields";
import { LeadFields } from "./LeadFields";
import { useCustomerForm } from "./useCustomerForm";
import type { CustomerFormProps } from "./CustomerFormTypes";
import { TagInput } from "../TagInput";
import { SmartAddressInput } from "../SmartAddressInput";
import { useAddressHierarchy } from "@/hooks/useAddressHierarchy";
import { useCustomerData } from "@/pages/customers/hooks/useCustomerData";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CustomerTypeToggle } from "./CustomerTypeToggle";
import { BusinessFields } from "./BusinessFields";
import { RepresentativeFields } from "./RepresentativeFields";

// Update the props type to include the new setCustomerToEdit prop
interface ExtendedCustomerFormProps extends CustomerFormProps {
  setCustomerToEdit?: (customerId: string | null) => void;
  /**
   * Controls layout behavior of the form wrapper.
   * - 'dialog' (default): caps content with max-h-[70vh] overflow-y-auto for use inside a Dialog.
   * - 'page': lets the form expand naturally, no inner scroll container.
   */
  layoutVariant?: 'page' | 'dialog';
}

export function CustomerForm({ customer, onSave, onCancel, setCustomerToEdit, layoutVariant = 'dialog' }: ExtendedCustomerFormProps) {
  const {
    form,
    companyMembers,
    cellFields,
    emailFields,
    appendCell,
    removeCell,
    appendEmail,
    removeEmail,
    watchedLeadSource,
    onSubmit,
    isSubmitting,
    duplicateRecord,
    setDuplicateRecord,
    prepareFormForDuplicateEdit
  } = useCustomerForm(customer, onSave);

  // Get existing customers for smart address suggestions
  const { customers } = useCustomerData();
  const addressHierarchy = useAddressHierarchy(customers);

  // Watch customer type
  const customerType = form.watch('customer_type') || 'residential';

  // Updated to fetch existing customer data and prepare form with merged data
  const handleEditExistingCustomer = (id: string) => {
    console.log("Editing existing customer with ID:", id);
    
    // Close current dialog and prepare for editing the existing customer
    setDuplicateRecord(null);
    
    // Close the current form/dialog
    onCancel();
    
    // Tell CustomersPage to load and edit this customer
    if (setCustomerToEdit) {
      // This function will now fetch the full customer record and prepare the form
      prepareFormForDuplicateEdit(id);
      setCustomerToEdit(id);
    }
  };

  // Get address label based on customer type
  const addressLabel = customerType === 'commercial' ? 'Business Address' : 'Address';

  const wrapperClass = layoutVariant === 'page' ? '' : 'max-h-[70vh] overflow-y-auto pr-2';

  return (
    <>
      <div className={wrapperClass}>
        <Form {...form}>
          <form onSubmit={onSubmit}>
            {/* Customer Type Toggle */}
            <CustomerTypeToggle form={form} />

            {/* Conditional fields based on customer type */}
            {customerType === 'residential' ? (
              <>
                {/* Residential: Name fields */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Residential: Contact info */}
                <ContactInfoFields
                  form={form}
                  cellFields={cellFields}
                  emailFields={emailFields}
                  appendCell={() => appendCell({ value: "" })}
                  removeCell={removeCell}
                  appendEmail={() => appendEmail({ value: "" })}
                  removeEmail={removeEmail}
                />
              </>
            ) : (
              <>
                {/* Commercial: Business fields */}
                <BusinessFields form={form} />
                
                {/* Commercial: Representative fields (internal use) */}
                <RepresentativeFields form={form} />
              </>
            )}

            {/* Address section (shared, but with different label) */}
            <div className="mt-4">
              <h3 className="text-lg font-medium mb-4">{addressLabel}</h3>
              <SmartAddressInput 
                form={form} 
                existingAddresses={customers}
                addressHierarchy={addressHierarchy}
                onAddressValidation={(field, isValid, suggestions) => {
                  console.log(`Address validation for ${field}:`, { isValid, suggestions });
                }}
              />
            </div>

            {/* Lead fields (shared) */}
            <LeadFields
              form={form}
              companyMembers={companyMembers}
              watchedLeadSource={watchedLeadSource}
            />

            {/* Tags (shared) */}
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Tags</FormLabel>
                  <FormControl>
                    <TagInput 
                      tags={field.value || []} 
                      onChange={field.onChange}
                      placeholder="Type and press Enter to add tags..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              onCancel={onCancel}
              isSubmitting={isSubmitting}
              submitLabel={customer ? 'Update Customer' : 'Create Customer'}
              submittingLabel={customer ? 'Updating...' : 'Creating...'}
            />

          </form>
        </Form>
      </div>
      
      <AlertDialog open={!!duplicateRecord} onOpenChange={() => setDuplicateRecord(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Customer Found</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicateRecord?.customerType === 'commercial' ? (
                <>A business with the same name, VAT number, email, or phone already exists: <strong>{duplicateRecord?.businessName}</strong></>
              ) : (
                <>A customer with the same email or phone number already exists: <strong>{duplicateRecord?.name} {duplicateRecord?.lastName}</strong></>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDuplicateRecord(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => duplicateRecord && handleEditExistingCustomer(duplicateRecord.id)}>
              <Edit className="w-4 h-4 mr-1" /> Edit Existing Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
